import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';

/**
 * RequestThread — shared between PartnerRequests and AdminRequests.
 * Renders a chat-bubble thread with a reply input.
 * "My" messages (from currentUser) appear right-aligned in gold.
 * "Their" messages appear left-aligned with sender label.
 */
export default function RequestThread({ request, currentUser, onMessageSent, t }) {
  const [text,    setText]    = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  // Auto-scroll to latest message whenever messages change
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [request.messages?.length]);

  const isMe = (msg) =>
    String(msg.sender?._id || msg.sender) === String(currentUser?._id);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await axios.post(`/api/requests/${request._id}/messages`, { text: text.trim() });
      await axios.post(`/api/requests/${request._id}/read`);
      setText('');
      onMessageSent();
    } catch (err) {
      console.error('Thread send error:', err);
    } finally {
      setSending(false);
    }
  };

  const messages = request.messages || [];

  return (
    <div style={{ marginTop: 16 }}>
      {/* Section label */}
      <div style={{
        fontSize: 11, color: 'var(--text-dim)', fontWeight: 600,
        textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10,
      }}>
        Thread · {messages.length} message{messages.length !== 1 ? 's' : ''}
      </div>

      {/* Message list */}
      <div style={{
        maxHeight: 280, overflowY: 'auto',
        display: 'flex', flexDirection: 'column', gap: 8,
        marginBottom: 12, paddingRight: 4,
      }}>
        {messages.length === 0 ? (
          <div style={{
            fontSize: 13, color: 'var(--text-dim)',
            textAlign: 'center', padding: '14px 0', fontStyle: 'italic',
          }}>
            {t('noFeedback')}
          </div>
        ) : messages.map((msg, i) => {
          const mine = isMe(msg);
          return (
            <div key={i} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start' }}>
              <div style={{
                maxWidth: '76%',
                background: mine ? 'var(--accent-glow)' : 'var(--surface)',
                border: `1px solid ${mine ? 'rgba(201,168,76,0.3)' : 'var(--border)'}`,
                borderRadius: mine ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                padding: '9px 13px',
              }}>
                {/* Sender label — only on "their" messages */}
                {!mine && (
                  <div style={{
                    fontSize: 10, color: 'var(--text-dim)', fontWeight: 600,
                    marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em',
                  }}>
                    {msg.senderName}
                    <span style={{ fontWeight: 400, marginLeft: 4, opacity: 0.6 }}>
                      · {msg.senderRole}
                    </span>
                  </div>
                )}

                {/* Message text */}
                <div style={{
                  fontSize: 13, color: 'var(--text)',
                  lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                }}>
                  {msg.text}
                </div>

                {/* Timestamp */}
                <div style={{
                  fontSize: 10, color: 'var(--text-dim)',
                  marginTop: 5, textAlign: mine ? 'right' : 'left',
                }}>
                  {new Date(msg.createdAt).toLocaleDateString('en-GB')}
                  {' '}
                  {new Date(msg.createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Reply input */}
      <form onSubmit={send} style={{ display: 'flex', gap: 8 }}>
        <input
          type="text"
          placeholder="Write a message…"
          value={text}
          onChange={e => setText(e.target.value)}
          style={{ flex: 1, fontSize: 13 }}
        />
        <button
          type="submit"
          className="btn-primary"
          style={{ padding: '9px 18px', fontSize: 13, flexShrink: 0 }}
          disabled={sending || !text.trim()}
        >
          {sending ? '…' : '↑ Send'}
        </button>
      </form>
    </div>
  );
}
